import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchCorrelation, fetchDemographics } from "./api";
import {
  components,
  correlate,
  projectPopulation,
  type AreaDemographics,
  type CommunePoint,
  type ComponentRow,
  type Correlation,
  type CorrelationX,
  type CorrelationY,
  type Projection,
  type ProjectionParams,
} from "./demographics";
import type { PriceLevel } from "../map/levels";

export interface AreaDemographicsResult {
  isLoading: boolean;
  isError: boolean;
  data: AreaDemographics | null;
  /** Growth split into natural change and net migration, period by period. */
  history: ComponentRow[];
  projection: Projection | null;
}

/**
 * One area's census history and its forward scenario.
 *
 * A neighbourhood is answered with its commune server-side, so the query key
 * is the code as clicked — two IRIS in the same commune do fetch twice, which
 * is a round-trip against a cache lookup that would have to know the mapping.
 */
export function useAreaDemographics(
  area: { scope: PriceLevel; code: string } | null,
  params: ProjectionParams,
): AreaDemographicsResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["demographics", area?.scope, area?.code],
    queryFn: () => fetchDemographics(area!.scope, area!.code),
    enabled: area !== null,
    staleTime: Infinity,
    retry: false,
  });

  const history = useMemo(() => (data ? components(data.series) : []), [data]);
  const projection = useMemo(
    () => (data ? projectPopulation(data, params) : null),
    [data, params],
  );

  return {
    isLoading: area !== null && isLoading,
    isError,
    data: data ?? null,
    history,
    projection,
  };
}

export interface CrossSection {
  isLoading: boolean;
  points: CommunePoint[];
  stats: Correlation;
  censusYear: number | null;
}

/**
 * The whole region's communes as one cross-section, filtered in the browser.
 *
 * The fetch is unfiltered and cached forever: switching measure, axis or
 * department is then instant, and the alternative — a query per combination —
 * would put a round-trip behind every click on a control.
 */
export function useCrossSection(
  x: CorrelationX,
  y: CorrelationY,
  filters: { minPop: number; dep: string },
): CrossSection {
  const { data, isLoading } = useQuery({
    queryKey: ["demographics", "correlation"],
    queryFn: fetchCorrelation,
    staleTime: Infinity,
    retry: false,
  });

  const points = useMemo(() => {
    const all = data?.communes ?? [];
    return all.filter(
      (p) =>
        p.pop >= filters.minPop &&
        (filters.dep === "all" || p.dep === filters.dep) &&
        Number.isFinite(p[x]) &&
        Number.isFinite(p[y]),
    );
  }, [data, x, y, filters.minPop, filters.dep]);

  const stats = useMemo(() => correlate(points, x, y), [points, x, y]);

  return { isLoading, points, stats, censusYear: data?.census_year ?? null };
}
